import { useEffect, MutableRefObject } from 'react';
import { Subject, Observable } from 'rxjs';
import { BigNum, PRICE_PRECISION_EXP } from '@velocity-exchange/sdk';
import {
	DlobWebsocketClient,
	ENUM_UTILS,
	MarketId,
	OrderbookGrouping,
} from '@velocity-exchange/common';
import { useQueryClient } from '@tanstack/react-query';
import useOrderbookDisplayStore, {
	OrderbookSource,
} from 'src/stores/orderbook/useOrderbookDisplayStore';
import { getOrderbookGroupingForMarket } from 'src/constants/marketDecimalConfig';
import useGetTickSizeForMarket from 'src/hooks/useGetTickSizeForMarket';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import useMarketStateStore from 'src/stores/useMarketStateStore';
import useSyncedRef from 'src/hooks/utils/useSyncedRef';
import { useShallow } from 'zustand/react/shallow';
import { dlog } from 'src/dev';
import { orderbookQueryKeys } from 'src/hooks/query/orderbook/orderbookQueryKeys';
import { L2OrderbookWithSlot } from 'src/hooks/query/orderbook/types';
import { TransformedMarketData } from '../utils/transformMarketData';
import { PRICING_QUERY_KEY } from 'src/hooks/query/pricing/usePricingData';
import { PricingData } from 'src/hooks/query/pricing/types';

interface UseOrderbookSubscriptionParams {
	client: DlobWebsocketClient;
	subscriptionRef: MutableRefObject<{ unsubscribe: () => void } | null>;
	currentSubscriptionMarketIdRef: MutableRefObject<MarketId | null>;
	currentSubscriptionGroupingRef: MutableRefObject<OrderbookGrouping | null>;
	destroyCurrentPipeline$: Subject<void>;
	createDataPipeline: (marketId: MarketId) => Observable<TransformedMarketData>;
	subscriptionRestartKey: number;
}

/**
 * Hook that manages the main orderbook subscription effect.
 * Sets up the data pipeline and handles market/grouping changes.
 */
export function useOrderbookSubscription({
	client,
	subscriptionRef,
	currentSubscriptionMarketIdRef,
	currentSubscriptionGroupingRef,
	destroyCurrentPipeline$,
	createDataPipeline,
	subscriptionRestartKey,
}: UseOrderbookSubscriptionParams): void {
	const queryClient = useQueryClient();
	const selectedMarket = useDriftStore((s) => s.selectedMarket);
	const setMarketStateStore = useMarketStateStore((s) => s.set);
	const getTickSizeForMarket = useGetTickSizeForMarket();

	const { groupingSizeSelection, orderbookSource, disableAutoSourceSwitching } =
		useOrderbookDisplayStore(
			useShallow((s) => ({
				groupingSizeSelection: s.groupingSizeSelection,
				orderbookSource: s.orderbookSource,
				disableAutoSourceSwitching: s.disableAutoSourceSwitching,
			}))
		);

	const {
		setOrderbookStore,
		resetOrderbookStore,
		setLoading,
		setWaitingForNewGroupingData,
	} = useOrderbookDisplayStore(
		useShallow((s) => ({
			setOrderbookStore: s.set,
			resetOrderbookStore: s.reset,
			setLoading: s.setLoading,
			setWaitingForNewGroupingData: s.setWaitingForNewGroupingData,
		}))
	);

	const groupingSizeSelectionRef = useSyncedRef(groupingSizeSelection);
	const orderbookSourceRef = useSyncedRef(orderbookSource);
	const disableAutoSourceSwitchingRef = useSyncedRef(
		disableAutoSourceSwitching
	);

	useEffect(() => {
		if (!selectedMarket.marketId) {
			// Clean up pipeline before unsubscribing
			destroyCurrentPipeline$.next();
			if (subscriptionRef.current) {
				subscriptionRef.current.unsubscribe();
				subscriptionRef.current = null;
			}
			if (currentSubscriptionMarketIdRef.current) {
				client.unsubscribeAll();
				currentSubscriptionMarketIdRef.current = null;
			}
			currentSubscriptionGroupingRef.current = null;
			resetOrderbookStore();
			return;
		}

		// Clean up websocket subscription when source is not websocket
		// (either manual switch via dev tools, or auto-fallback from timeout)
		if (
			!ENUM_UTILS.match(orderbookSourceRef.current, OrderbookSource.WEBSOCKET)
		) {
			// Clean up pipeline before unsubscribing
			destroyCurrentPipeline$.next();
			if (subscriptionRef.current) {
				subscriptionRef.current.unsubscribe();
				subscriptionRef.current = null;
			}
			if (currentSubscriptionMarketIdRef.current) {
				client.unsubscribeAll();
				currentSubscriptionMarketIdRef.current = null;
			}
			currentSubscriptionGroupingRef.current = null;
			return;
		}

		const marketId = selectedMarket.marketId;

		// Check if already subscribed
		if (
			currentSubscriptionMarketIdRef.current?.equals(marketId) &&
			currentSubscriptionGroupingRef.current ===
				groupingSizeSelectionRef.current
		) {
			return;
		}

		// Clean up previous
		if (subscriptionRef.current) {
			destroyCurrentPipeline$.next();
			subscriptionRef.current.unsubscribe();
			subscriptionRef.current = null;
		}

		// Determine grouping
		let newGrouping = groupingSizeSelectionRef.current;
		const storeMarketId = useOrderbookDisplayStore.getState().marketId;
		const isTabRecovery =
			!currentSubscriptionMarketIdRef.current &&
			storeMarketId?.equals(marketId);

		if (
			!isTabRecovery &&
			!currentSubscriptionMarketIdRef.current?.equals(marketId)
		) {
			const baseAssetSymbol =
				selectedMarket.current?.uiSymbols.baseAssetSymbol || '';
			const tickSizeBN = getTickSizeForMarket(marketId);
			const tickSizeInDollars = BigNum.from(
				tickSizeBN,
				PRICE_PRECISION_EXP
			).toNum();
			const isValidTickSize = tickSizeInDollars > 0.000001;
			newGrouping = getOrderbookGroupingForMarket(
				baseAssetSymbol,
				isValidTickSize ? tickSizeInDollars : undefined
			);
		}

		// Update store
		setOrderbookStore((s) => {
			const isMarketChange = !s.marketId?.equals(marketId);
			const isGroupingChange = s.groupingSizeSelection !== newGrouping;

			if (isMarketChange) {
				s.marketId = marketId;
				s.loading = true;
				s.waitingForNewGroupingData = false;
				s.groupingSizeSelection = newGrouping;
			} else if (isGroupingChange) {
				s.waitingForNewGroupingData = true;
			}
		});

		// Subscribe
		client.subscribeToMarkets([{ marketId, grouping: newGrouping }]);
		client.resetSlotTracking();

		currentSubscriptionMarketIdRef.current = marketId;
		currentSubscriptionGroupingRef.current = newGrouping;

		// Set up pipeline
		const subscription = createDataPipeline(marketId).subscribe({
			next: ({
				l2Orderbook,
				marketPriceState,
				oracleData,
				mmOracleData,
				slot,
			}) => {
				if (
					ENUM_UTILS.match(
						orderbookSourceRef.current,
						OrderbookSource.WEBSOCKET
					)
				) {
					const l2OrderbookWithSlot: L2OrderbookWithSlot = {
						...l2Orderbook,
						slot,
					};
					queryClient.setQueryData(
						orderbookQueryKeys.l2Orderbook(marketId.key),
						l2OrderbookWithSlot
					);
					queryClient.setQueryData(
						orderbookQueryKeys.marketPriceState(marketId.key),
						marketPriceState
					);

					// Also update pricing cache so all components using usePricingData get high-frequency updates for current market
					// Use functional updater to avoid losing concurrent updates
					queryClient.setQueryData<Map<string, PricingData>>(
						PRICING_QUERY_KEY,
						(prev) => {
							const newPricingMap = new Map(prev);
							const existingPricingData = newPricingMap.get(marketId.key);
							newPricingMap.set(marketId.key, {
								symbol: existingPricingData?.symbol ?? '',
								marketIndex: marketId.marketIndex,
								marketType: marketId.marketType,
								oraclePrice: BigNum.from(
									oracleData.price,
									PRICE_PRECISION_EXP
								).toNum(),
								markPrice: marketPriceState.markPrice.toNum(),
								lastPrice: existingPricingData?.lastPrice ?? 0,
								updateTimestamp: Date.now(),
							});
							return newPricingMap;
						}
					);

					setLoading(false);
					setWaitingForNewGroupingData(false);

					setMarketStateStore((s) => {
						s.latestUpdateSlot = Math.max(
							s.latestUpdateSlot,
							oracleData.slot.toNumber()
						);
						s.marketDataState[marketId.key] = {
							...s.marketDataState[marketId.key],
							derivedState: {
								...s.marketDataState[marketId.key]?.derivedState,
								...marketPriceState,
								updateSlot: oracleData.slot.toNumber(),
							},
							oracle: {
								...s.marketDataState[marketId.key]?.oracle,
								price: BigNum.from(oracleData.price, PRICE_PRECISION_EXP),
								slot: oracleData.slot,
								confidence: oracleData.confidence,
								hasSufficientNumberOfDataPoints:
									oracleData.hasSufficientNumberOfDataPoints,
								twap: BigNum.from(oracleData.twap, PRICE_PRECISION_EXP),
								twapConfidence: oracleData.twapConfidence,
								maxPrice: BigNum.from(oracleData.maxPrice, PRICE_PRECISION_EXP),
							},
							mmOracle: mmOracleData
								? {
										...s.marketDataState[marketId.key]?.mmOracle,
										price: BigNum.from(mmOracleData.price, PRICE_PRECISION_EXP),
										slot: mmOracleData.slot,
										confidence: mmOracleData.confidence,
										hasSufficientNumberOfDataPoints:
											mmOracleData.hasSufficientNumberOfDataPoints,
										isMMOracleActive: mmOracleData.isMMOracleActive,
									}
								: undefined,
						};
					});
				}
			},
			error: (error) => {
				dlog('orderbook_websocket_subscription', 'Subscription error', {
					error,
				});
			},
		});

		subscriptionRef.current = subscription;
	}, [
		selectedMarket.marketId?.key,
		client,
		resetOrderbookStore,
		createDataPipeline,
		setOrderbookStore,
		setLoading,
		setWaitingForNewGroupingData,
		groupingSizeSelection,
		orderbookSource,
		disableAutoSourceSwitching,
		subscriptionRestartKey,
		setMarketStateStore,
		queryClient,
		destroyCurrentPipeline$,
		getTickSizeForMarket,
		subscriptionRef,
		currentSubscriptionMarketIdRef,
		currentSubscriptionGroupingRef,
		groupingSizeSelectionRef,
		orderbookSourceRef,
		disableAutoSourceSwitchingRef,
	]);
}
